import type { Item } from "../api-client/models/item";

// Informal Settlements are system-placed only (they appear automatically
// when a nation runs out of housing) and can never be proposed by a
// student — see PURCHASE_ITEM's additionalCheck in config/permissions.ts,
// which always blocks them. Kept out of the Development catalog entirely
// rather than shown as a permanently-locked card. See the Help page for
// player-facing context on why they show up on the map.
export const INFORMAL_SETTLEMENT_ITEM_NAMES = [
  "Informal Settlement",
  "Large Informal Settlement",
];

// Groups the flat Development/Military catalogs into labeled sections and
// (for the Village -> City -> Megalopolis chain) an explicit tier order.
// There's no category field on Item in the API today, so this is a
// client-side-only mapping by item name, matching how prerequisite checks
// are already done in src/config/permissions.ts (PURCHASE_ITEM).

export type InfrastructureCategory =
  | "housing"
  | "resource_production"
  | "water_infrastructure"
  | "public_services"
  | "diplomacy_trade"
  | "defense";

export const INFRASTRUCTURE_CATEGORY_ORDER: {
  key: InfrastructureCategory;
  label: string;
}[] = [
  { key: "housing", label: "Housing" },
  { key: "resource_production", label: "Resource Production" },
  { key: "water_infrastructure", label: "Water Infrastructure" },
  { key: "public_services", label: "Public Services" },
  { key: "diplomacy_trade", label: "Diplomacy & Trade" },
  { key: "defense", label: "Defense" },
];

const INFRASTRUCTURE_CATEGORY_BY_NAME: Record<string, InfrastructureCategory> = {
  Village: "housing",
  City: "housing",
  Megalopolis: "housing",
  "Informal Settlement": "housing",
  "Large Informal Settlement": "housing",

  "Mineral Mine": "resource_production",
  Sawmill: "resource_production",
  "Land Oil Rig": "resource_production",
  "Ocean Oil Rig": "resource_production",
  Tractor: "resource_production",
  "Fishing Boat": "resource_production",

  "Water Well": "water_infrastructure",
  Reservoir: "water_infrastructure",
  "Water Filtration": "water_infrastructure",
  "Desalination Plant": "water_infrastructure",

  Hospital: "public_services",
  Library: "public_services",
  University: "public_services",
  Landfill: "public_services",

  Embassy: "diplomacy_trade",
  "Trade Port": "diplomacy_trade",

  "Military Base": "defense",
};

// Village -> City -> Megalopolis is the one real upgrade chain in the
// catalog today. Order here also drives sort order within the Housing group.
const HOUSING_TIER_BY_NAME: Record<string, number> = {
  Village: 1,
  City: 2,
  Megalopolis: 3,
};

export function getInfrastructureCategory(item: Item): InfrastructureCategory {
  return INFRASTRUCTURE_CATEGORY_BY_NAME[item.name] ?? "public_services";
}

export function getHousingTier(item: Item): number | undefined {
  return HOUSING_TIER_BY_NAME[item.name];
}

export type MilitaryCategory = "ground" | "air" | "naval" | "strategic";

export const MILITARY_CATEGORY_ORDER: { key: MilitaryCategory; label: string }[] = [
  { key: "ground", label: "Ground" },
  { key: "air", label: "Air" },
  { key: "naval", label: "Naval" },
  { key: "strategic", label: "Strategic" },
];

const MILITARY_CATEGORY_BY_NAME: Record<string, MilitaryCategory> = {
  Troops: "ground",
  "Alpha Jet": "air",
  "Beta Jet": "air",
  "Alpha Submarine": "naval",
  "Beta Submarine": "naval",
  "Nuclear Weapon": "strategic",
  "Nuclear Defense": "strategic",
};

export function getMilitaryCategory(item: Item): MilitaryCategory {
  return MILITARY_CATEGORY_BY_NAME[item.name] ?? "ground";
}

// Generic grouping helper shared by the Development and Military pages.
export function groupItemsByCategory<TCategory extends string, TItem>(
  items: TItem[],
  order: { key: TCategory; label: string }[],
  getCategory: (item: TItem) => TCategory,
): { key: TCategory; label: string; items: TItem[] }[] {
  const byCategory = new Map<TCategory, TItem[]>();
  for (const item of items) {
    const category = getCategory(item);
    const bucket = byCategory.get(category);
    if (bucket) {
      bucket.push(item);
    } else {
      byCategory.set(category, [item]);
    }
  }
  return order
    .map(({ key, label }) => ({ key, label, items: byCategory.get(key) ?? [] }))
    .filter((group) => group.items.length > 0);
}
